import {PasswordInput} from '@mantine/core';
import React, {useState} from 'react';

import {MCP_PRODUCT_NAME} from 'llm/NamingConstants';
import notifications from 'notifications';
import FormGroup from 'ui-library/bs/FormGroup';
import FormInput from 'ui-library/bs/FormInput';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useFormInput from 'ui-library/hooks/useFormInput';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {postJSONPromise} from 'util/Net';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';

interface AddMCPServerModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

type TransportType = 'STREAMABLE_HTTP' | 'SSE';

function getTransportTypeDisplayName(transportType: TransportType): string {
  return transportType === 'STREAMABLE_HTTP' ? 'Streamable HTTP' : 'SSE';
}

export default function AddMCPServerModal(props: AddMCPServerModalProps): JSX.Element {
  const nameInput = useFormInput('');
  const serverUrlInput = useFormInput('');
  const [transportType, setTransportType] = useState<TransportType>('STREAMABLE_HTTP');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Manual registration state
  const [useManualRegistration, setUseManualRegistration] = useState(false);
  const clientIdInput = useFormInput('');
  const clientSecretInput = useFormInput('');

  // Construct the redirect URI from the current origin
  const redirectUri = `${window.location.origin}/1/api/llm/mcp/oauth/callback`;

  const isBaseValid = nameInput.value.trim() !== '' && serverUrlInput.value.trim() !== '';
  const isManualRegistrationValid =
    !useManualRegistration || (clientIdInput.value.trim() !== '' && clientSecretInput.value.trim() !== '');
  const isValid = isBaseValid && isManualRegistrationValid;

  async function handleSubmit(): Promise<void> {
    try {
      setIsSubmitting(true);
      const data: Record<string, string> = {
        name: nameInput.value.trim(),
        serverUrl: serverUrlInput.value.trim(),
        transportType,
      };

      if (useManualRegistration) {
        data.clientId = clientIdInput.value.trim();
        data.clientSecret = clientSecretInput.value.trim();
      }

      await withLoadingScreen(
        postJSONPromise({
          url: '/1/api/llm/mcp-server-configs',
          data,
        }),
        `Adding ${MCP_PRODUCT_NAME}...`
      );
      notifications.success(`${MCP_PRODUCT_NAME} added successfully`);
      props.onRequestSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Add ${MCP_PRODUCT_NAME}`}
      onRequestSubmit={handleSubmit}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          text: 'Add',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isValid || isSubmitting,
        },
        {text: 'Cancel', cancel: true},
      ]}
    >
      <p style={{marginBottom: 16, color: 'var(--text-secondary)'}}>
        Get details from MCP servers that your team hosts or from an external tool you trust to add an{' '}
        {MCP_PRODUCT_NAME}.
      </p>
      <FormGroup>
        <TextField
          label="Name"
          placeholder="My MCP Server"
          {...nameInput}
          autoFocus={true}
          maxLength={255}
          isRequired={true}
        />
      </FormGroup>
      <FormGroup>
        <TextField
          label="Server URL"
          placeholder="https://mcp.example.com/mcp"
          {...serverUrlInput}
          maxLength={2048}
          isRequired={true}
        />
      </FormGroup>
      <FormGroup>
        <FieldLabel label="Transport Type" isRequired={true} />
        <SelectDropdown
          displayName={getTransportTypeDisplayName(transportType)}
          menu={
            <Menu value={transportType}>
              <Option
                value="STREAMABLE_HTTP"
                onSelect={() => {
                  setTransportType('STREAMABLE_HTTP');
                }}
              >
                {getTransportTypeDisplayName('STREAMABLE_HTTP')}
              </Option>
              <Option
                value="SSE"
                onSelect={() => {
                  setTransportType('SSE');
                }}
              >
                {getTransportTypeDisplayName('SSE')}
              </Option>
            </Menu>
          }
        />
      </FormGroup>

      {/* Advanced: Manual Registration */}
      <div
        style={{
          marginTop: 16,
          padding: 16,
          backgroundColor: GRAY_LIGHT,
          borderRadius: 4,
        }}
      >
        <CheckboxInput
          id="manual-registration-checkbox"
          checked={useManualRegistration}
          onChange={(e: InputChangeEvent) => {
            setUseManualRegistration(e.target.checked);
          }}
          label="Use manual OAuth registration (advanced)"
        />
        <p style={{marginTop: 8, marginBottom: 0, fontSize: 12, color: 'var(--text-secondary)'}}>
          Enable this option if the MCP server requires you to manually register an OAuth application in their
          developer console.
        </p>

        {useManualRegistration && (
          <div style={{marginTop: 16}}>
            <FormGroup>
              <Label>Redirect URI (click to copy)</Label>
              <FormInput
                readOnly={true}
                selectAllOnClick={true}
                value={redirectUri}
                onClick={() => {
                  copyToClipboard(redirectUri, {
                    showToastOnSuccess: true,
                    toastText: 'Redirect URI copied to clipboard!',
                  });
                }}
                style={{cursor: 'copy'}}
              />
              <p style={{marginTop: 4, marginBottom: 0, fontSize: 12, color: 'var(--text-secondary)'}}>
                Copy this URI and paste it into the MCP server&apos;s developer console when creating your
                OAuth application.
              </p>
            </FormGroup>
            <FormGroup>
              <TextField
                label="Client ID"
                placeholder="Enter the client ID from the MCP server"
                {...clientIdInput}
                maxLength={2048}
                isRequired={true}
              />
            </FormGroup>
            <FormGroup>
              <Label>Client Secret*</Label>
              <PasswordInput
                placeholder="Enter the client secret from the MCP server"
                value={clientSecretInput.value}
                onChange={clientSecretInput.onChange}
                required={true}
                maxLength={2048}
              />
            </FormGroup>
          </div>
        )}
      </div>
    </Modal>
  );
}
